import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Flex } from 'core/components';

import { Remarkable } from 'remarkable';
import remarkableExtLink from 'remarkable-extlink';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import { themeGet } from 'styled-system';
import { getFontSizeCSS, getBackgroundColorCSS } from './text/textPanelConstants';

const md = new Remarkable({ html: true });
md.use(remarkableExtLink, { host: window.location.host });

md.renderer.rules.table_open = () => `<table class="${Classes.HTML_TABLE} ${Classes.HTML_TABLE_STRIPED}">`;

const getDefaultContent = () => '*No content*';

const MarkdownContainer = styled.div`
  > *:last-child {
    margin-bottom: 0;
  }

  > *:only-child {
    margin: 0;
    padding: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    margin-top: 0;
  }

  code {
    font-family: ${themeGet('fonts.monospace')};
    padding: 2px 4px;
    font-size: 90%;
    color: ${themeGet('colors.primary')};
    background-color: ${themeGet('colors.primaryBackground')};
    border-radius: 4px;
  }

  pre {
    display: block;
    padding: 8px;
    margin: 0 0 12px;
    word-break: break-all;
    word-wrap: break-word;
    background-color: ${themeGet('colors.sectionBackground')};
    border: ${themeGet('borders.thin')};
    border-radius: 4px;

    code {
      padding: 0;
      font-size: 14px;
      line-height: 1.4;
      color: ${themeGet('colors.muted')};
      background-color: transparent;
      white-space: pre-wrap;
      border-radius: 0;
    }
  }

  table {
    margin-bottom: 12px;
  }

  blockquote {
    border-left: ${themeGet('borders.thick')};
    margin: 12px 0;
    padding-left: 12px;
    padding-block: 6px;
  }
`;

@observer
class TextPanel extends Component {
  render() {
    const { config } = this.props;

    const content = config.content || getDefaultContent();
    const backgroundColor = getBackgroundColorCSS(config.backgroundColor || 'transparent');
    const fontSize = getFontSizeCSS(config.fontSize);
    const textAlign = config.textAlign || 'left';
    const verticalAlign = config.verticalAlign || 'top';

    const justifyMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
    const justifyContent = justifyMap[verticalAlign] || 'flex-start';

    // Force dark text when a light background color is explicitly set
    const color = config.backgroundColor && config.backgroundColor !== 'transparent' ? '#1a1a1a' : undefined;

    return (
      <Flex
        flexDirection="column"
        justifyContent={justifyContent}
        p={2}
        flex={1}
        style={{ backgroundColor, fontSize, textAlign, color }}
      >
        {/* eslint-disable-next-line react/no-danger */}
        <MarkdownContainer dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(md.render(content)) }} />
      </Flex>
    );
  }
}

export default TextPanel;
